/**
 * Catalog reads shared across instances through the Next data cache
 * (FRIDGE-1615 for the trending pool, FRIDGE-1241 option C for the rest).
 *
 * A catalog read that returns the same rows to every caller with the same
 * arguments does not need the database on every render. Measured 10/17/2026
 * (pg_stat_statements, 8.4 days): three such reads were 26% of all database
 * statement time, uncached: the popular-products read (13.2%), a collection's
 * first product image (9.5%) and the editor's picks read (3.3%). A crawler
 * burst on fresh instances multiplies them, and the database cancels its own
 * statements under that load (the anonymous role's 3 s timeout).
 *
 * Two layers:
 *   - readThroughDataCache: the Next data cache, shared by every instance and
 *     kept across deployments. One revalidation a day, the products tag for an
 *     on-demand refresh. A failed read throws inside the cached function, so a
 *     failure is never stored. Outside a Next server (tests, scripts, a browser
 *     bundle) there is no incremental cache and the read runs directly.
 *   - readSharedCatalog: the same, plus one in-flight read per key on each
 *     instance, so concurrent renders on a cold key ask the database once.
 *
 * Callers must treat the returned rows as read-only: concurrent callers of
 * readSharedCatalog receive the same value.
 */
import { unstable_cache } from 'next/cache'

/**
 * One day: the longest ISR window of any page that renders these reads
 * (articles, product, part, filter, collection, local and rebate pages are
 * 86400; the brand, blog and rebate indexes are shorter). In a render the
 * LOWEST revalidate wins, so a shorter cache window would re-render those
 * pages more often. Pinned by tests/catalog-read-data-cache-fridge-1241.test.ts.
 */
export const SHARED_CATALOG_REVALIDATE_S = 86400

export async function readThroughDataCache<T>(namespace: string, key: string, read: () => Promise<T>): Promise<T> {
  try {
    return await unstable_cache(read, [namespace, key], { revalidate: SHARED_CATALOG_REVALIDATE_S, tags: ['products'] })()
  } catch (err) {
    if (err instanceof Error && /incrementalCache/.test(err.message)) return read()
    throw err
  }
}

const inflight = new Map<string, Promise<unknown>>()

export function readSharedCatalog<T>(namespace: string, key: string, read: () => Promise<T>): Promise<T> {
  const id = `${namespace}\u0000${key}`
  const pending = inflight.get(id)
  if (pending) return pending as Promise<T>
  const created: Promise<T> = readThroughDataCache(namespace, key, read).finally(() => {
    if (inflight.get(id) === created) inflight.delete(id)
  })
  inflight.set(id, created)
  return created
}

/** Test seam: forget every in-flight read, as a fresh instance would. */
export function resetSharedCatalogForTests(): void {
  inflight.clear()
}
